import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { QueryFilterMultipleSelectWithAutoComplete } from './query-filter-multiple-select'
import { Button } from '@/components/ui/button'
import { Input } from '../ui/input'
import { QueryFilterCounties } from './query-filter-counties'
import { useMutation } from '@tanstack/react-query'
import { filterCriteriaFilterCriteriaPostMutation } from '@/generated/@tanstack/react-query.gen'
import { Loader2 } from 'lucide-react'
import { isEmpty } from 'lodash'
import useAbility from '@/hooks/use-ability'
import { CustomLabelWithLock } from '../ui/custom-label-with-lock'
import { useQueryFilterStore } from './query-filter-store'
import { UseFormReturn } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useEffect } from 'react'
import { ETierKey } from '@/pages/subscribe/_data/subscribe-constant'
import { useQueryState } from 'nuqs'
import {
  TRIAL_LIMIT_QUERY_KEY,
  TrialLimitType,
} from '../trial-modal/trial-limit-modal'

const limitList = [10, 50, 100, 250, 500]

export const queryFilterFormSchema = z.object({
  counties: z.array(z.string()),
  owners: z.array(z.string()).default([]),
  gsas: z.array(z.string()).default([]),
  water_districts: z.array(z.string()).default([]),
  main_crops: z.array(z.string()).default([]),
  max_hs: z.number().max(100).default(100),
  min_hs: z.number().min(0).default(0),
  max_ss: z.number().max(100).default(100),
  min_ss: z.number().min(0).default(0),
  record_limit: z.number().max(500).default(10),
  max_acreage: z.number().optional(),
  min_acreage: z.number().optional(),
})

export type QueryFilterFormType = z.infer<typeof queryFilterFormSchema>

interface Props {
  onSubmitDone: (data: any) => void
  isTutorial?: boolean
  form: UseFormReturn<QueryFilterFormType>
}
export function QueryFilterModelForm({
  isTutorial,
  form,
  onSubmitDone,
}: Props) {
  const { setFormData } = useQueryFilterStore()
  const { checkAbility } = useAbility()
  const { getAbilityLimit, invalidateQuery: invalidateUserAbility } =
    useAbility()

  const authData = localStorage.getItem('auth')
  const isDemoUser = authData
    ? JSON.parse(authData).sessionKey === 'a6cf9f6e-3341-4996-8d6a-83e826b67adf'
    : false
  const isFreeTrial = authData
    ? JSON.parse(authData).tier === ETierKey.Trial
    : false

  const isEnableOwnerName = checkAbility('ownership_information') || isDemoUser
  const isEnableHydraScore = checkAbility('hydra_score') || isDemoUser
  const isEnableSoilScore = checkAbility('soil_score') || isDemoUser

  const { formData } = useQueryFilterStore()

  const [_, setTrialLimitState] = useQueryState(TRIAL_LIMIT_QUERY_KEY)

  useEffect(() => {
    form.setValue('counties', formData.counties)
    form.setValue('owners', formData.owners)
    form.setValue('gsas', formData.gsas)
    form.setValue('water_districts', formData.water_districts)
    form.setValue('main_crops', formData.main_crops)
    form.setValue('max_hs', formData.max_hs)
    form.setValue('min_hs', formData.min_hs)
    form.setValue('max_ss', formData.max_ss)
    form.setValue('min_ss', formData.min_ss)
    form.setValue('record_limit', formData.record_limit)
    form.setValue('max_acreage', formData.max_acreage)
    form.setValue('min_acreage', formData.min_acreage)
    form.trigger()
  }, [])

  const { mutate, isPending } = useMutation({
    ...filterCriteriaFilterCriteriaPostMutation({}),
    onError: (error) => {
      toast.error(error.toString())
    },
    onSuccess: (res: any) => {
      toast.success(
        `${res.result.features.length} parcel(s) returned from the search. (Parcels without a Hydra score are not included in the results)`
      )
      setFormData(form.getValues())
      onSubmitDone(res.result)
      invalidateUserAbility()
    },
  })

  function onFormSubmit(values: QueryFilterFormType) {
    if (isTutorial) return
    const trialLimit = getAbilityLimit('number_of_remaining_criteria_search')
    if (trialLimit === 0 && isFreeTrial) {
      setTrialLimitState(TrialLimitType.CRITERIA_SEARCH_LIMIT)
      return
    }
    mutate({
      body: {
        ...values,
        max_acreage: values.max_acreage ? values.max_acreage.toString() : '',
        min_acreage: values.min_acreage ? values.min_acreage.toString() : '',
        session_key: JSON.parse(localStorage.auth).sessionKey,
      },
    })
  }

  const isEmptyCounties = isEmpty(form.watch('counties'))

  function onRemoveCounty(county: string) {
    form.setValue('owners', [])
    form.setValue('gsas', [])
    form.setValue('water_districts', [])
    form.setValue('main_crops', [])
  }

  const onClickDisableLimitTrial = () =>
    setTrialLimitState(TrialLimitType.FILTER_RESULTS_LIMIT)

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-2">
        <FormField
          control={form.control}
          name="counties"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px] w-full">
              <CustomLabelWithLock title="County:" hasAbility={true} />
              <FormControl>
                <QueryFilterCounties
                  onRemoveCounty={onRemoveCounty}
                  selected={field.value}
                  setSelected={(v) => {
                    field.onChange(v)
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="record_limit"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px]">
              <CustomLabelWithLock title="Limit Results:" hasAbility={true} />
              <FormControl>
                <div className="flex flex-row gap-2 items-center">
                  {limitList.map((l) => {
                    const isDisabled = isFreeTrial && l !== 10
                    return (
                      <div
                        key={`radio-${l}`}
                        className="flex flex-row gap-1 items-center"
                      >
                        <input
                          type="radio"
                          className={`w-4 h-4 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} text-primary`}
                          checked={field.value === l}
                          onChange={() => {
                            if (isDisabled) {
                              return onClickDisableLimitTrial()
                            }
                            field.onChange(l)
                          }}
                        />
                        <p
                          className={`font-normal text-base ${isDisabled ? 'opacity-50' : ''}`}
                        >
                          {l.toString()}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="owners"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px]">
              <CustomLabelWithLock
                title="Owner Name:"
                hasAbility={isEnableOwnerName}
              />
              <FormControl>
                <QueryFilterMultipleSelectWithAutoComplete
                  disabled={isEmptyCounties || !isEnableOwnerName}
                  isTutorial={false}
                  counties={form.watch('counties')}
                  queryType="owner"
                  selected={field.value}
                  setSelected={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gsas"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px]">
              <CustomLabelWithLock title="GSA:" hasAbility={true} />
              <FormControl>
                <QueryFilterMultipleSelectWithAutoComplete
                  isTutorial={false}
                  disabled={isEmptyCounties}
                  counties={form.watch('counties')}
                  queryType="gsa"
                  selected={field.value}
                  setSelected={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="water_districts"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px]">
              <CustomLabelWithLock title="Water District:" hasAbility={true} />
              <FormControl>
                <QueryFilterMultipleSelectWithAutoComplete
                  isTutorial={false}
                  disabled={isEmptyCounties}
                  counties={form.watch('counties')}
                  queryType="wd"
                  selected={field.value}
                  setSelected={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="main_crops"
          render={({ field }) => (
            <FormItem className="space-y-1 max-w-[560px]">
              <CustomLabelWithLock title="Main Crop:" hasAbility={true} />
              <FormControl>
                <QueryFilterMultipleSelectWithAutoComplete
                  isTutorial={false}
                  disabled={isEmptyCounties}
                  counties={form.watch('counties')}
                  queryType="main_crop"
                  selected={field.value}
                  setSelected={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="min_hs"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px]">
                <CustomLabelWithLock
                  title="Min HydraScore:"
                  hasAbility={isEnableHydraScore}
                />
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    disabled={isEmptyCounties || !isEnableHydraScore}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (value < 0) {
                        return field.onChange(0)
                      }
                      if (value > 100) {
                        return field.onChange(100)
                      }
                      return field.onChange(value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_hs"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px]">
                <CustomLabelWithLock
                  title="Max HydraScore:"
                  hasAbility={isEnableHydraScore}
                />
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    disabled={isEmptyCounties || !isEnableHydraScore}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (value < 0) {
                        return field.onChange(0)
                      }
                      if (value > 100) {
                        return field.onChange(100)
                      }
                      return field.onChange(value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="min_ss"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px]">
                <CustomLabelWithLock
                  title="Min SoilScore:"
                  hasAbility={isEnableSoilScore}
                />
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    disabled={isEmptyCounties || !isEnableSoilScore}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (value < 0) {
                        return field.onChange(0)
                      }
                      if (value > 100) {
                        return field.onChange(100)
                      }
                      return field.onChange(value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_ss"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px]">
                <CustomLabelWithLock
                  title="Max SoilScore:"
                  hasAbility={isEnableSoilScore}
                />
                <FormControl>
                  <Input
                    disabled={isEmptyCounties || !isEnableSoilScore}
                    type="number"
                    {...field}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (value > 100) {
                        return field.onChange(100)
                      }
                      return field.onChange(value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="min_acreage"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px]">
                <CustomLabelWithLock title="Min Acreage:" hasAbility={true} />
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    disabled={isEmptyCounties}
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_acreage"
            render={({ field }) => (
              <FormItem className="space-y-1 max-w-[560px]">
                <CustomLabelWithLock title="Max Acreage:" hasAbility={true} />
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    disabled={isEmptyCounties}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (value > 100) {
                        return field.onChange(100)
                      }
                      return field.onChange(value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="pt-4 flex flex-row justify-end w-full">
          <Button
            className="disabled:bg-[#808080] disabled:opacity-100 disabled:cursor-not-allowed"
            size={'md'}
            type="submit"
            disabled={isPending || isEmptyCounties}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <p>Run Query</p>
          </Button>
        </div>
      </form>
    </Form>
  )
}
